import type React from 'react';
import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { ChairSides, SeatingTable } from '@peles/shared';
import type { CanvasItem } from '../types';
import { isTableItem } from '../types';
import { ftToPx } from '../helpers';
import { seatDropId, tableDropId } from '../../seating/dropTargets';
import { tableTitle } from '../../seating/boardModel';
import { initialsOf, seatSpots, type SeatSpot } from './seatSpots';
import type { FloorPlanSeatingMode } from './useFloorPlanSeatingMode';

/** dnd-kit's refs are typed for HTML; an SVG element measures the same way. */
type SvgRef<T extends SVGElement> = React.Ref<T>;
const svgRef = <T extends SVGElement>(ref: (el: HTMLElement | null) => void) =>
  ref as unknown as SvgRef<T>;

const stop = (e: React.MouseEvent) => e.stopPropagation();

/**
 * The floor plan's Seating mode drawn over the canvas: every table the seating
 * knows is a drop target with how full it is above it, and each of its chairs
 * a smaller one carrying the initials of whoever sits there. Tables and chairs
 * take a tap as well as a drop, so a phone seats guests the same way.
 */
export function SeatingCanvasLayer({
  mode,
  items,
  chairSizeFt,
  chairSides,
}: {
  mode: FloorPlanSeatingMode;
  items: CanvasItem[];
  chairSizeFt: number;
  chairSides: ChairSides;
}) {
  const state = mode.state;
  const tables = useMemo(() => {
    if (!state) return [];
    const byUid = new Map(state.tables.map((t) => [t.uid, t]));
    return items.flatMap((item) => {
      const table = isTableItem(item) && item.uid ? byUid.get(item.uid) : undefined;
      return table
        ? [
            {
              item,
              table,
              spots: seatSpots(item, items, {
                chairSizeFt,
                chairSides,
                occupied: table.occupied.map((seat) => seat.seatNumber),
              }),
            },
          ]
        : [];
    });
  }, [state, items, chairSizeFt, chairSides]);
  if (!state) return null;
  return (
    <g data-testid="seating-canvas-layer">
      {tables.map(({ item, table, spots }) => (
        <SeatingTableSpot
          key={table.uid}
          item={item}
          table={table}
          spots={spots}
          picking={!!mode.selected}
          onTapTable={mode.tapTable}
          onTapSeat={mode.tapSeat}
        />
      ))}
    </g>
  );
}

function SeatingTableSpot({
  item,
  table,
  spots,
  picking,
  onTapTable,
  onTapSeat,
}: {
  item: CanvasItem;
  table: SeatingTable;
  spots: SeatSpot[];
  picking: boolean;
  onTapTable: (uid: string) => void;
  onTapSeat: (uid: string, seatNumber: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: tableDropId(table.uid) });
  const w = ftToPx(item.widthFt);
  const h = ftToPx(item.heightFt);
  const title = tableTitle(table);
  const taken = table.occupied.length;
  const over = taken > table.seats;
  const full = !over && table.seats > 0 && taken === table.seats;
  // The badge sits above the table and its chairs, level whatever the table's turn.
  const top = Math.min(
    item.y + h / 2 - Math.max(w, h) / 2,
    ...spots.map((spot) => spot.y - spot.r),
  );
  const badgeX = item.x + w / 2;
  const badgeY = top - 12;
  const badge = `${taken}/${table.seats}`;
  const badgeW = 10 + badge.length * 7;

  return (
    <g data-testid={`seating-spot-${table.uid}`}>
      <g transform={`translate(${item.x}, ${item.y}) rotate(${item.rotation}, ${w / 2}, ${h / 2})`}>
        <rect
          ref={svgRef<SVGRectElement>(setNodeRef)}
          width={w}
          height={h}
          rx={8}
          role="button"
          aria-label={`${title}: ${taken} of ${table.seats} seats taken`}
          className={
            isOver
              ? 'fill-primary-500/20 stroke-primary-600'
              : picking
                ? 'fill-primary-500/10 stroke-primary-400'
                : 'fill-transparent stroke-transparent'
          }
          strokeWidth={2}
          strokeDasharray={picking && !isOver ? '6 4' : undefined}
          style={{ cursor: picking ? 'copy' : 'default' }}
          onMouseDown={stop}
          onClick={(e) => {
            e.stopPropagation();
            onTapTable(table.uid);
          }}
          data-testid={`seating-table-hit-${table.uid}`}
        />
      </g>

      <g pointerEvents="none" data-testid={`canvas-occupancy-${table.uid}`}>
        <rect
          x={badgeX - badgeW / 2}
          y={badgeY - 9}
          width={badgeW}
          height={18}
          rx={9}
          className={over ? 'fill-red-600' : full ? 'fill-green-600' : 'fill-white stroke-gray-300'}
          strokeWidth={1}
        />
        <text
          x={badgeX}
          y={badgeY}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          fontWeight={600}
          className={over || full ? 'fill-white' : 'fill-gray-800'}
        >
          {badge}
        </text>
      </g>

      {spots.map((spot) => (
        <SeatMarker
          key={spot.seatNumber}
          table={table}
          title={title}
          spot={spot}
          picking={picking}
          onTap={onTapSeat}
        />
      ))}
    </g>
  );
}

function SeatMarker({
  table,
  title,
  spot,
  picking,
  onTap,
}: {
  table: SeatingTable;
  title: string;
  spot: SeatSpot;
  picking: boolean;
  onTap: (uid: string, seatNumber: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: seatDropId(table.uid, spot.seatNumber) });
  const occupant = table.occupied.find((seat) => seat.seatNumber === spot.seatNumber);
  const r = Math.max(spot.r, 7);
  const label = occupant
    ? `Seat ${spot.seatNumber} at ${title}: ${occupant.name}`
    : `Seat ${spot.seatNumber} at ${title} (empty)`;
  return (
    <g
      role="button"
      aria-label={label}
      onMouseDown={stop}
      onClick={(e) => {
        e.stopPropagation();
        onTap(table.uid, spot.seatNumber);
      }}
      style={{ cursor: occupant || picking ? 'pointer' : 'default' }}
      data-testid={`canvas-seat-${table.uid}-${spot.seatNumber}`}
    >
      <title>{label}</title>
      <circle
        ref={svgRef<SVGCircleElement>(setNodeRef)}
        cx={spot.x}
        cy={spot.y}
        r={r}
        strokeWidth={isOver ? 2 : 1}
        strokeDasharray={!occupant && picking && !isOver ? '3 2' : undefined}
        className={
          isOver
            ? 'fill-primary-200 stroke-primary-700'
            : occupant
              ? occupant.locked
                ? 'fill-primary-800 stroke-primary-900'
                : 'fill-primary-600 stroke-primary-700'
              : picking
                ? 'fill-white stroke-primary-500'
                : 'fill-white/60 stroke-gray-300'
        }
      />
      {occupant && (
        <text
          x={spot.x}
          y={spot.y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={Math.max(7, Math.min(10, r))}
          fontWeight={700}
          pointerEvents="none"
          className="fill-white"
        >
          {initialsOf(occupant.name)}
        </text>
      )}
    </g>
  );
}
